import React, { useState, useMemo } from 'react';
import { X, Loader2, Upload, ImageIcon, Info, RotateCcw, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import SquareRelinkPanel from '@/components/admin/SquareRelinkPanel';

// ── Constants ────────────────────────────────────────────────────────────────

const REPORTING_CATEGORIES = [
  { value: 'sim_rigs',         label: 'Sim Rigs' },
  { value: 'steering_wheels',  label: 'Steering Wheels' },
  { value: 'pedals',           label: 'Pedals' },
  { value: 'motion_platforms', label: 'Motion Platforms' },
  { value: 'monitors',         label: 'Monitors' },
  { value: 'accessories',      label: 'Accessories / Sim Rig Brackets' },
  { value: 'gaming_computers', label: 'Gaming Computers' },
];

const PLATFORMS = [
  { value: 'pc',      label: 'PC' },
  { value: 'console', label: 'Console' },
  { value: 'both',    label: 'Both' },
];

const VISIBILITY_OPTIONS = [
  { value: 'VISIBLE',     label: 'Visible' },
  { value: 'HIDDEN',      label: 'Hidden' },
  { value: 'UNAVAILABLE', label: 'Unavailable' },
];

// UK VAT rates. Food and drink is not one bucket — plain biscuits, tea and
// coffee are zero-rated while soft drinks, confectionery and crisps are
// standard-rated — so the rate is set per product.
const VAT_RATE_OPTIONS = [
  { value: 'T0',  label: 'T0 — Zero-rated (0%)' },
  { value: 'T5',  label: 'T5 — Reduced (5%)' },
  { value: 'T20', label: 'T20 — Standard (20%)' },
];

// eBay listing options (UK marketplace). Used by the eBay section + the API/CSV export.
const EBAY_DURATIONS = [
  { value: 'GTC',     label: 'GTC (Good \'Til Cancelled)' },
  { value: 'Days_7',  label: '7 Days' },
  { value: 'Days_10', label: '10 Days' },
  { value: 'Days_30', label: '30 Days' },
];

const EBAY_SHIPPING_SERVICES = [
  { value: 'UK_RoyalMailTracked24',        label: 'Royal Mail Tracked 24' },
  { value: 'UK_RoyalMailTracked48',        label: 'Royal Mail Tracked 48' },
  { value: 'UK_RoyalMailFirstClassStandard', label: 'Royal Mail 1st Class' },
  { value: 'UK_RoyalMailSecondClassStandard', label: 'Royal Mail 2nd Class' },
  { value: 'UK_RoyalMailSpecialDeliveryNextDay', label: 'Royal Mail Special Delivery' },
  { value: 'UK_CollectPlusStandard',       label: 'Collect+' },
  { value: 'UK_HermesTrackedShipping',     label: 'Evri Tracked' },
  { value: 'UK_DPDNextDay',                label: 'DPD Next Day' },
  { value: 'UK_Parcelforce24',             label: 'Parcelforce 24' },
  { value: 'UK_OtherCourier',              label: 'Other Courier' },
  { value: 'UK_Freight',                   label: 'Freight / Pallet (large items)' },
];

const EBAY_RETURNS_WITHIN = [
  { value: 'Days_14', label: '14 Days' },
  { value: 'Days_30', label: '30 Days' },
  { value: 'Days_60', label: '60 Days' },
];

const EBAY_RETURN_PAID_BY = [
  { value: 'Buyer',  label: 'Buyer pays' },
  { value: 'Seller', label: 'Seller pays (free returns)' },
];

// Fields that Square owns and will overwrite on next sync
const SQUARE_OWNED_FIELDS = new Set([
  'name', 'customer_facing_name', 'description', 'reporting_category',
  'variation_name', 'sku', 'gtin', 'price', 'option_name', 'option_value',
  'weight', 'weight_unit', 'shipping_enabled', 'delivery_enabled', 'pickup_enabled',
  'stockable', 'sellable', 'stock_quantity', 'in_stock',
  'square_online_visibility', 'seo_title', 'seo_description', 'permalink',
  'image_url', 'image_url_2', 'image_url_3', 'image_url_4',
]);

const slugify = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

const EMPTY = {
  square_item_id: '', square_variation_id: '', square_parent_item_id: '', is_variant: false, manual_product: false,
  name: '', customer_facing_name: '', description: '',
  reporting_category: 'Sim Rigs', category: 'sim_rigs',
  variation_name: '', sku: '', gtin: '', price: '',
  option_name: '', option_value: '',
  weight: '', weight_unit: 'g',
  length: '', width: '', height: '', dimension_unit: 'mm',
  shipping_enabled: false, delivery_enabled: false, pickup_enabled: false,
  stock_quantity: '', stockable: true, sellable: true, in_stock: true,
  square_online_visibility: 'VISIBLE',
  seo_title: '', seo_description: '', permalink: '',
  image_url: '', image_url_2: '', image_url_3: '', image_url_4: '',
  published: true, featured: false,
  platform: 'both', specs: '',
  ebay_category_id: '', ebay_listing_duration: 'GTC', ebay_shipping_service: 'UK_OtherCourier',
  ebay_shipping_cost: 0, ebay_dispatch_time_max: 2, ebay_returns_accepted: true,
  ebay_returns_within: 'Days_30', ebay_return_shipping_paid_by: 'Buyer',
  ebay_item_location: 'Liverpool', ebay_item_postcode: '', ebay_item_country: 'GB',
  ebay_paypal_email: '', ebay_listed: false, ebay_listing_id: '',
  ebay_variation_aspect: 'Colour', ebay_group_key: '',
  custom_slug: '',
  discounts_disabled: false,
  vat_rate: 'T20',
  lead_time_days: '',
  cost_price_ex_vat: '',
};

// ── Style helpers ─────────────────────────────────────────────────────────────

const inputCls     = "bg-[#0d0d0f] border-white/10 text-white h-11 placeholder:text-white/20 focus:border-primary/60";
const modInputCls  = "bg-[#0d0d0f] border-yellow-500/40 text-white h-11 placeholder:text-white/20 focus:border-yellow-400/60";
const readonlyCls  = "bg-[#0a0a0c] border-white/5 text-white/30 h-11 cursor-not-allowed select-none";
const labelCls     = "text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block";
const sectionCls   = "pt-5 mt-5 border-t border-white/5 space-y-4";
const sectionLabel = "text-primary text-[9px] font-bold tracking-[0.18em] mb-3 block uppercase";

// ── Sub-components ────────────────────────────────────────────────────────────

const Toggle = ({ value, onChange, label, modified, onReset }) => (
  <div className="flex items-center gap-2.5">
    <label className="flex items-center gap-2.5 cursor-pointer">
      <div onClick={() => onChange(!value)}
        className={`w-10 h-5 rounded-full transition-colors relative ${value ? 'bg-primary' : 'bg-white/10'} ${modified ? 'ring-1 ring-yellow-500/50' : ''}`}>
        <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${value ? 'translate-x-5' : 'translate-x-0.5'}`} />
      </div>
      <span className={`text-xs font-bold tracking-wider ${modified ? 'text-yellow-400' : 'text-white/60'}`}>{label}</span>
    </label>
    {modified && onReset && (
      <button type="button" onClick={onReset} title="Reset to Square value"
        className="text-yellow-500/60 hover:text-yellow-400 transition-colors">
        <RotateCcw className="w-3 h-3" />
      </button>
    )}
  </div>
);

// Wraps a field with modified indicator and reset button
const FieldWrapper = ({ label, required, hint, modified, onReset, children }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <p className={`${labelCls} mb-0`}>
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
        {hint && <span className="normal-case font-normal text-white/20 ml-1">{hint}</span>}
      </p>
      {modified && onReset && (
        <button type="button" onClick={onReset}
          className="flex items-center gap-1 text-[10px] text-yellow-500/70 hover:text-yellow-400 transition-colors shrink-0 ml-2"
          title="Reset to last Square value">
          <RotateCcw className="w-2.5 h-2.5" /> Reset
        </button>
      )}
    </div>
    {modified && (
      <p className="text-yellow-500/60 text-[10px] mb-1 flex items-center gap-1">
        <AlertTriangle className="w-2.5 h-2.5" /> Locally modified — will be overwritten on next Square sync
      </p>
    )}
    {children}
  </div>
);

// ── Main Component ────────────────────────────────────────────────────────────

export default function ProductFormModal({ product, onClose, onSaved, preset }) {
  const isEdit = !!product;

  // Fields a re-link has just re-read from Square become the new baseline, so
  // the refreshed values don't show up as local edits that would be reverted.
  const [squareBaseline, setSquareBaseline] = useState({});

  // The "original" Square values — used for reset and diff. For a brand-new
  // product, merge in any `preset` (e.g. a pre-seeded variation of a parent).
  const original = useMemo(() => {
    const base = product ? {
      ...EMPTY,
      ...product,
      price: product.price ?? '',
      stock_quantity: product.stock_quantity ?? '',
      weight: product.weight ?? '',
    } : { ...EMPTY, ...(preset || {}) };
    return { ...base, ...squareBaseline };
  }, [product, preset, squareBaseline]);

  const [form, setForm] = useState({ ...original });
  const queryClient = useQueryClient();

  // Called by the re-link panel with the fields Square just returned.
  const applySquareRefresh = (applied) => {
    setSquareBaseline((prev) => ({ ...prev, ...applied }));
    setForm((f) => ({ ...f, ...applied }));
    queryClient.invalidateQueries({ queryKey: ['admin-products'] });
    queryClient.invalidateQueries({ queryKey: ['products'] });
  };
  const [saving, setSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(null); // which slot: 1-4
  const [error, setError] = useState('');

  // Live storefront categories so newly created categories pull through here
  const { data: dbCategories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: () => base44.entities.Category.list('sort_order'),
  });
  const categoryOptions = useMemo(() => {
    const map = new Map();
    for (const c of dbCategories) if (c.key) map.set(c.key, { value: c.key, label: c.label });
    for (const r of REPORTING_CATEGORIES) if (!map.has(r.value)) map.set(r.value, r);
    return Array.from(map.values());
  }, [dbCategories]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const reset = (k) => setForm(f => ({ ...f, [k]: original[k] }));

  // Whether a field differs from the saved Square value
  const isModified = (k) => {
    if (!isEdit) return false;
    if (!SQUARE_OWNED_FIELDS.has(k)) return false;
    const a = form[k], b = original[k];
    // coerce to comparable strings
    return String(a ?? '') !== String(b ?? '');
  };

  const getInputCls = (k) => isModified(k) ? modInputCls : inputCls;

  // Any modified Square-owned fields?
  const hasLocalOverrides = isEdit && [...SQUARE_OWNED_FIELDS].some(k => isModified(k));

  const setCategoryEnum = (val) => {
    const match = categoryOptions.find(r => r.value === val);
    setForm(f => ({
      ...f,
      category: val,
      reporting_category: match ? match.label : f.reporting_category,
    }));
  };

  const handleImageUpload = async (e, slot) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageUploading(slot);
    const { file_url } = await base44.integrations.Core.UploadFile({ file });
    const key = slot === 1 ? 'image_url' : `image_url_${slot}`;
    set(key, file_url);
    setImageUploading(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || form.price === '' || !form.category) {
      setError('Item Name, Price, and Category are required.');
      return;
    }
    if (!form.manual_product && !form.square_item_id) {
      setError('Square Item ID is required for Square-managed products. Enable "Manual / One-off Product" if this item is not in Square.');
      return;
    }
    setError('');
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price) || 0,
      stock_quantity: form.stock_quantity === '' || form.stock_quantity === null ? null : parseInt(form.stock_quantity, 10),
      weight: form.weight === '' || form.weight === null ? null : parseFloat(form.weight) || null,
      lead_time_days: form.lead_time_days === '' || form.lead_time_days === null ? null : parseInt(form.lead_time_days, 10),
      cost_price_ex_vat: form.cost_price_ex_vat === '' || form.cost_price_ex_vat === null ? null : parseFloat(form.cost_price_ex_vat) || 0,
    };
    if (isEdit) {
      await base44.entities.Product.update(product.id, payload);
    } else {
      await base44.entities.Product.create(payload);
    }
    setSaving(false);
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="bg-[#111115] border border-white/10 w-full max-w-2xl max-h-[92vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#111115] z-10">
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest">
              {isEdit ? 'EDIT PRODUCT' : 'NEW PRODUCT'}
            </p>
            {isEdit && <p className="text-white/30 text-xs mt-0.5 truncate max-w-md">{product.name}</p>}
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Sync notice */}
        <div className="flex items-start gap-2.5 mx-6 mt-4 px-4 py-3 bg-yellow-500/5 border border-yellow-500/20">
          <Info className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
          <p className="text-yellow-300/70 text-xs leading-relaxed">
            Square is the primary catalog. Fields marked with <AlertTriangle className="inline w-2.5 h-2.5 text-yellow-500" /> are <span className="text-yellow-400 font-bold">locally modified</span> and <span className="font-bold">will be overwritten on the next Square sync</span>. Use Reset to restore the Square value.
          </p>
        </div>

        {hasLocalOverrides && (
          <div className="mx-6 mt-2 px-4 py-2.5 bg-yellow-500/10 border border-yellow-500/30 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
            <p className="text-yellow-300 text-xs font-bold">This product has local overrides. They will be lost on the next Square sync.</p>
          </div>
        )}

        <form onSubmit={handleSave} className="px-6 pb-6">

          {/* ── SQUARE IDENTITY ──────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Square Identity <span className="text-white/20 normal-case font-normal">(read-only after creation)</span></span>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className={labelCls}>TOKEN (ITEM) <span className="text-red-400">*</span></p>
                <Input value={form.square_item_id} onChange={e => set('square_item_id', e.target.value)}
                  className={isEdit ? readonlyCls : inputCls} readOnly={isEdit} placeholder="e.g. ABCDEF123456" />
              </div>
              <div>
                <p className={labelCls}>TOKEN (VARIATION)</p>
                <Input value={form.square_variation_id || ''} onChange={e => set('square_variation_id', e.target.value)}
                  className={isEdit ? readonlyCls : inputCls} readOnly={isEdit} placeholder="Blank for standalone" />
              </div>
              <div>
                <p className={labelCls}>PARENT ITEM TOKEN</p>
                <Input value={form.square_parent_item_id || ''} onChange={e => set('square_parent_item_id', e.target.value)}
                  className={isEdit ? readonlyCls : inputCls} readOnly={isEdit} placeholder="For variant products" />
              </div>
              <div className="flex items-end pb-2">
                <Toggle value={form.is_variant} onChange={v => set('is_variant', v)} label="IS VARIANT" />
              </div>
            </div>
            {isEdit && <SquareRelinkPanel product={product} onRefreshed={applySquareRefresh} />}
          </div>

          {/* ── ITEM DETAILS ──────────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Item Details <span className="text-white/20 normal-case font-normal">(Square: Item-level fields)</span></span>

            <FieldWrapper label="ITEM NAME" required hint='Square "Item Name"' modified={isModified('name')} onReset={() => reset('name')}>
              <Input value={form.name} onChange={e => set('name', e.target.value)}
                className={getInputCls('name')} placeholder="e.g. Simucube Ethernet Hub Mount" />
            </FieldWrapper>

            <FieldWrapper label="CUSTOMER-FACING NAME" hint='Square "Customer-Facing Name"' modified={isModified('customer_facing_name')} onReset={() => reset('customer_facing_name')}>
              <Input value={form.customer_facing_name || ''} onChange={e => set('customer_facing_name', e.target.value)}
                className={getInputCls('customer_facing_name')} placeholder="Shown in Square Online storefront" />
            </FieldWrapper>

            <FieldWrapper label="DESCRIPTION" hint="HTML supported" modified={isModified('description')} onReset={() => reset('description')}>
              <textarea value={form.description || ''} onChange={e => set('description', e.target.value)}
                className={`w-full bg-[#0d0d0f] border text-white text-sm placeholder:text-white/20 focus:outline-none px-3 py-2.5 resize-none h-24 font-mono text-xs transition-colors ${isModified('description') ? 'border-yellow-500/40 focus:border-yellow-400/60' : 'border-white/10 focus:border-primary/60'}`}
                placeholder="<p>Product description...</p>" />
            </FieldWrapper>
          </div>

          {/* ── VARIATION / OPTION ────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Variation / Option <span className="text-white/20 normal-case font-normal">(Square: Variation-level fields)</span></span>

            <div className="grid grid-cols-2 gap-4">
              <FieldWrapper label="VARIATION NAME" hint='Square "Variation Name"' modified={isModified('variation_name')} onReset={() => reset('variation_name')}>
                <Input value={form.variation_name || ''} onChange={e => set('variation_name', e.target.value)}
                  className={getInputCls('variation_name')} placeholder="e.g. ZYXEL, Standard" />
              </FieldWrapper>
              <FieldWrapper label="SKU" modified={isModified('sku')} onReset={() => reset('sku')}>
                <Input value={form.sku || ''} onChange={e => set('sku', e.target.value)}
                  className={getInputCls('sku')} placeholder="e.g. SC-HUB-001" />
              </FieldWrapper>
              <FieldWrapper label="GTIN / BARCODE" hint='Square "GTIN"' modified={isModified('gtin')} onReset={() => reset('gtin')}>
                <Input value={form.gtin || ''} onChange={e => set('gtin', e.target.value)}
                  className={getInputCls('gtin')} placeholder="e.g. 0123456789012" />
              </FieldWrapper>
              <FieldWrapper label="PRICE (GBP)" required modified={isModified('price')} onReset={() => reset('price')}>
                <Input type="number" step="0.01" min="0" value={form.price}
                  onChange={e => set('price', e.target.value)}
                  className={getInputCls('price')} placeholder="0.00" />
              </FieldWrapper>
              <div>
                <p className={labelCls}>COST PRICE (EX VAT) <span className="normal-case font-normal text-white/20">(£ — pushed to Square)</span></p>
                <Input type="number" step="0.01" min="0" value={form.cost_price_ex_vat ?? ''}
                  onChange={e => set('cost_price_ex_vat', e.target.value)}
                  className={inputCls} placeholder="0.00" />
                <p className="text-white/30 text-[10px] mt-1.5 leading-relaxed">Unit cost excluding VAT. Sent to Square as the item's cost so its COGS and margin reporting work, and drives the margin columns in Stock Control.</p>
              </div>
              <FieldWrapper label="OPTION NAME 1" hint='Square "Option Name 1"' modified={isModified('option_name')} onReset={() => reset('option_name')}>
                <Input value={form.option_name || ''} onChange={e => set('option_name', e.target.value)}
                  className={getInputCls('option_name')} placeholder="e.g. Colour, Size" />
              </FieldWrapper>
              <FieldWrapper label="OPTION VALUE 1" hint='Square "Option Value 1"' modified={isModified('option_value')} onReset={() => reset('option_value')}>
                <Input value={form.option_value || ''} onChange={e => set('option_value', e.target.value)}
                  className={getInputCls('option_value')} placeholder="e.g. Red, Large" />
              </FieldWrapper>
            </div>
          </div>

          {/* ── CATEGORY ──────────────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Category</span>
            <div className="grid grid-cols-2 gap-4">
              <FieldWrapper label="REPORTING CATEGORY" required hint='Square "Reporting Category"' modified={isModified('reporting_category')} onReset={() => reset('reporting_category')}>
                <select value={form.category} onChange={e => setCategoryEnum(e.target.value)}
                  className={`w-full bg-[#0d0d0f] border text-white text-sm h-11 px-3 focus:outline-none transition-colors ${isModified('reporting_category') ? 'border-yellow-500/40 focus:border-yellow-400/60' : 'border-white/10 focus:border-primary/60'}`}>
                  {categoryOptions.map(c => (
                    <option key={c.value} value={c.value} className="bg-[#111115]">{c.label}</option>
                  ))}
                </select>
              </FieldWrapper>
              <div>
                <p className={labelCls}>BASE44 CATEGORY ENUM <span className="normal-case font-normal text-white/20">(storefront routing)</span></p>
                <Input value={form.category} readOnly className={readonlyCls} />
              </div>
            </div>
          </div>

          {/* ── TAX ───────────────────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Tax <span className="text-white/20 normal-case font-normal">(Base44 only — not sent to Square)</span></span>
            <div>
              <p className={labelCls}>VAT RATE</p>
              <select value={form.vat_rate || 'T20'} onChange={e => set('vat_rate', e.target.value)}
                className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm h-11 px-3 focus:outline-none focus:border-primary/60 transition-colors">
                {VAT_RATE_OPTIONS.map(o => <option key={o.value} value={o.value} className="bg-[#111115]">{o.label}</option>)}
              </select>
              <p className="text-white/30 text-[10px] mt-1.5 leading-relaxed">Applied to this product's till lines and its Sage export row. Zero-rate plain biscuits, tea and coffee; standard-rate soft drinks, confectionery and crisps.</p>
            </div>
          </div>

          {/* ── INVENTORY ─────────────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Inventory <span className="text-white/20 normal-case font-normal">(Square-owned)</span></span>
            <FieldWrapper label="CURRENT QUANTITY" hint="blank = unlimited / untracked" modified={isModified('stock_quantity')} onReset={() => reset('stock_quantity')}>
              <Input type="number" min="0" value={form.stock_quantity}
                onChange={e => set('stock_quantity', e.target.value)}
                className={getInputCls('stock_quantity')} placeholder="Unlimited" />
            </FieldWrapper>
            <div className="flex flex-wrap gap-6">
              <Toggle value={form.stockable !== false} onChange={v => set('stockable', v)} label="STOCKABLE"
                modified={isModified('stockable')} onReset={() => reset('stockable')} />
              <Toggle value={form.sellable !== false} onChange={v => set('sellable', v)} label="SELLABLE"
                modified={isModified('sellable')} onReset={() => reset('sellable')} />
              <Toggle value={form.in_stock !== false} onChange={v => set('in_stock', v)} label="IN STOCK"
                modified={isModified('in_stock')} onReset={() => reset('in_stock')} />
            </div>
          </div>

          {/* ── FULFILMENT ────────────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Fulfilment <span className="text-white/20 normal-case font-normal">(Square-owned)</span></span>
            <div className="flex flex-wrap gap-6">
              <Toggle value={form.shipping_enabled === true} onChange={v => set('shipping_enabled', v)} label="SHIPPING ENABLED"
                modified={isModified('shipping_enabled')} onReset={() => reset('shipping_enabled')} />
              <Toggle value={form.delivery_enabled === true} onChange={v => set('delivery_enabled', v)} label="DELIVERY ENABLED"
                modified={isModified('delivery_enabled')} onReset={() => reset('delivery_enabled')} />
              <Toggle value={form.pickup_enabled === true} onChange={v => set('pickup_enabled', v)} label="PICKUP ENABLED"
                modified={isModified('pickup_enabled')} onReset={() => reset('pickup_enabled')} />
            </div>
          </div>

          {/* ── WEIGHT ────────────────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Weight <span className="text-white/20 normal-case font-normal">(Square "Weight")</span></span>
            <div className="grid grid-cols-2 gap-4">
              <FieldWrapper label="WEIGHT" hint="Square 'Weight'" modified={isModified('weight')} onReset={() => reset('weight')}>
                <Input type="number" min="0" step="0.01" value={form.weight || ''}
                  onChange={e => set('weight', e.target.value)}
                  className={getInputCls('weight')} placeholder="e.g. 1200" />
              </FieldWrapper>
              <FieldWrapper label="WEIGHT UNIT" modified={isModified('weight_unit')} onReset={() => reset('weight_unit')}>
                <select value={form.weight_unit || 'g'} onChange={e => set('weight_unit', e.target.value)}
                  className={`w-full bg-[#0d0d0f] border text-white text-sm h-11 px-3 focus:outline-none transition-colors ${isModified('weight_unit') ? 'border-yellow-500/40' : 'border-white/10 focus:border-primary/60'}`}>
                  {['g','kg','lb','oz'].map(u => <option key={u} value={u} className="bg-[#111115]">{u}</option>)}
                </select>
              </FieldWrapper>
            </div>
          </div>

          {/* ── DIMENSIONS (INTERNAL) ──────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Dimensions (internal) <span className="text-white/20 normal-case font-normal">(not sent to Click & Drop — stored for our records only)</span></span>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <p className={labelCls}>LENGTH</p>
                <Input type="number" min="0" step="0.1" value={form.length ?? ''}
                  onChange={e => set('length', e.target.value === '' ? '' : parseFloat(e.target.value))}
                  className={inputCls} placeholder="0" />
              </div>
              <div>
                <p className={labelCls}>WIDTH</p>
                <Input type="number" min="0" step="0.1" value={form.width ?? ''}
                  onChange={e => set('width', e.target.value === '' ? '' : parseFloat(e.target.value))}
                  className={inputCls} placeholder="0" />
              </div>
              <div>
                <p className={labelCls}>HEIGHT</p>
                <Input type="number" min="0" step="0.1" value={form.height ?? ''}
                  onChange={e => set('height', e.target.value === '' ? '' : parseFloat(e.target.value))}
                  className={inputCls} placeholder="0" />
              </div>
              <div>
                <p className={labelCls}>UNIT</p>
                <select value={form.dimension_unit || 'mm'} onChange={e => set('dimension_unit', e.target.value)}
                  className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm h-11 px-3 focus:outline-none focus:border-primary/60 transition-colors">
                  {['mm','cm','m','in'].map(u => <option key={u} value={u} className="bg-[#111115]">{u}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* ── SQUARE ONLINE ─────────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Square Online <span className="text-white/20 normal-case font-normal">(Square-owned)</span></span>
            <div className="grid grid-cols-2 gap-4">
              <FieldWrapper label="ITEM VISIBILITY" hint='Square "Square Online Item Visibility"' modified={isModified('square_online_visibility')} onReset={() => reset('square_online_visibility')}>
                <select value={form.square_online_visibility || 'VISIBLE'} onChange={e => set('square_online_visibility', e.target.value)}
                  className={`w-full bg-[#0d0d0f] border text-white text-sm h-11 px-3 focus:outline-none transition-colors ${isModified('square_online_visibility') ? 'border-yellow-500/40' : 'border-white/10 focus:border-primary/60'}`}>
                  {VISIBILITY_OPTIONS.map(o => <option key={o.value} value={o.value} className="bg-[#111115]">{o.label}</option>)}
                </select>
              </FieldWrapper>
              <FieldWrapper label="PERMALINK" hint='Square "Permalink"' modified={isModified('permalink')} onReset={() => reset('permalink')}>
                <Input value={form.permalink || ''} onChange={e => set('permalink', e.target.value)}
                  className={getInputCls('permalink')} placeholder="e.g. /my-product" />
              </FieldWrapper>
            </div>
            <FieldWrapper label="SEO TITLE" hint='Square "SEO Title"' modified={isModified('seo_title')} onReset={() => reset('seo_title')}>
              <Input value={form.seo_title || ''} onChange={e => set('seo_title', e.target.value)}
                className={getInputCls('seo_title')} placeholder="Page title for search engines" />
            </FieldWrapper>
            <FieldWrapper label="SEO DESCRIPTION" hint='Square "SEO Description"' modified={isModified('seo_description')} onReset={() => reset('seo_description')}>
              <textarea value={form.seo_description || ''} onChange={e => set('seo_description', e.target.value)}
                className={`w-full bg-[#0d0d0f] border text-white text-sm placeholder:text-white/20 focus:outline-none px-3 py-2.5 resize-none h-16 transition-colors ${isModified('seo_description') ? 'border-yellow-500/40' : 'border-white/10 focus:border-primary/60'}`}
                placeholder="Meta description for search engines" />
            </FieldWrapper>
          </div>

          {/* ── IMAGE GALLERY ─────────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Image Gallery <span className="text-white/20 normal-case font-normal">(Square: up to 4 images)</span></span>
            <div className="grid grid-cols-2 gap-4">
              {[
                { slot: 1, key: 'image_url',   label: 'Image URL 1 (Primary)' },
                { slot: 2, key: 'image_url_2', label: 'Image URL 2' },
                { slot: 3, key: 'image_url_3', label: 'Image URL 3' },
                { slot: 4, key: 'image_url_4', label: 'Image URL 4' },
              ].map(({ slot, key, label }) => (
                <div key={key}>
                  <div className="flex items-center justify-between mb-1.5">
                    <p className={`${labelCls} mb-0`}>{label}</p>
                    {isModified(key) && (
                      <button type="button" onClick={() => reset(key)}
                        className="flex items-center gap-1 text-[10px] text-yellow-500/70 hover:text-yellow-400 transition-colors">
                        <RotateCcw className="w-2.5 h-2.5" /> Reset
                      </button>
                    )}
                  </div>
                  {isModified(key) && (
                    <p className="text-yellow-500/60 text-[10px] mb-1 flex items-center gap-1">
                      <AlertTriangle className="w-2.5 h-2.5" /> Locally modified
                    </p>
                  )}
                  <div className="flex gap-2">
                    <div className="w-12 h-12 bg-[#0d0d0f] border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                      {form[key]
                        ? <img src={form[key]} alt="" className="w-full h-full object-cover" />
                        : <ImageIcon className="w-4 h-4 text-white/20" />}
                    </div>
                    <div className="flex-1 space-y-1">
                      <label className="cursor-pointer flex items-center gap-1.5 bg-[#0d0d0f] border border-white/10 hover:border-white/30 text-white/50 hover:text-white text-[10px] font-bold tracking-wider px-3 py-2 transition-all">
                        {imageUploading === slot ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                        {imageUploading === slot ? 'UPLOADING...' : 'UPLOAD'}
                        <input type="file" accept="image/*" className="hidden"
                          onChange={e => handleImageUpload(e, slot)} disabled={imageUploading !== null} />
                      </label>
                      <Input value={form[key] || ''} onChange={e => set(key, e.target.value)}
                        className={`${isModified(key) ? modInputCls : inputCls} text-[11px] h-8`}
                        placeholder="or paste URL" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── BASE44 OVERRIDES ──────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>Base44 Overrides <span className="text-white/20 normal-case font-normal">(not in Square — safe from sync overwrites)</span></span>

            {/* Manual product toggle */}
            <div className={`px-4 py-3 border ${form.manual_product ? 'border-blue-500/30 bg-blue-500/5' : 'border-white/5 bg-white/[0.02]'}`}>
              <Toggle
                value={form.manual_product === true}
                onChange={v => set('manual_product', v)}
                label="MANUAL / ONE-OFF PRODUCT"
              />
              <p className="text-white/30 text-xs mt-2 leading-relaxed">
                {form.manual_product
                  ? <span className="text-blue-400">This product is <strong>not managed by Square</strong>. It will never be overwritten, deleted, or affected by any Square sync or re-import.</span>
                  : 'Enable this for products intentionally created in Base44 that do not exist in Square.'}
              </p>
            </div>

            <div className={`px-4 py-3 border ${form.discounts_disabled ? 'border-amber-500/30 bg-amber-500/5' : 'border-white/5 bg-white/[0.02]'}`}>
              <Toggle
                value={form.discounts_disabled === true}
                onChange={v => set('discounts_disabled', v)}
                label="EXCLUDE FROM DISCOUNTS / VOUCHERS"
              />
              <p className="text-white/30 text-xs mt-2 leading-relaxed">
                {form.discounts_disabled
                  ? <span className="text-amber-400">This product <strong>cannot be discounted</strong>. Any voucher code entered at checkout will only apply to the other items in the cart.</span>
                  : 'Tick this for items that must never be sold with a discount or voucher code applied.'}
              </p>
            </div>

            <div>
              <p className={labelCls}>LEAD TIME (DAYS) <span className="normal-case font-normal text-white/20">(made-to-order items)</span></p>
              <Input type="number" min="0" step="1" value={form.lead_time_days ?? ''}
                onChange={e => set('lead_time_days', e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                className={inputCls} placeholder="e.g. 14" />
              <p className="text-white/30 text-[10px] mt-1.5 leading-relaxed">Shown on the product page when this item is out of stock. Leave blank to show "Contact us for lead time".</p>
            </div>

            <div>
              <p className={labelCls}>STORE URL SLUG <span className="normal-case font-normal text-white/20">(friendly web address)</span></p>
              <div className="flex gap-2">
                <Input value={form.custom_slug || ''} onChange={e => set('custom_slug', slugify(e.target.value))} className={inputCls} placeholder="e.g. simucube-premium-stop-button-mount" />
                <button type="button" onClick={() => set('custom_slug', slugify(form.name))} className="shrink-0 border border-white/10 hover:border-white/30 text-white/50 hover:text-white text-[10px] font-bold tracking-wider px-3 transition-all">FROM NAME</button>
              </div>
              <p className="text-white/30 text-[10px] mt-1.5">Public URL: <span className="text-primary font-mono break-all">/shop/{form.custom_slug || (isEdit ? product?.id : '<id>')}</span></p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className={labelCls}>PLATFORM</p>
                <select value={form.platform || 'both'} onChange={e => set('platform', e.target.value)}
                  className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm h-11 px-3 focus:outline-none focus:border-primary/60 transition-colors">
                  {PLATFORMS.map(p => <option key={p.value} value={p.value} className="bg-[#111115]">{p.label}</option>)}
                </select>
              </div>
              <div className="flex flex-col gap-3 justify-end pb-1">
                <Toggle value={form.published !== false} onChange={v => set('published', v)} label="PUBLISHED (visible in store)" />
                <Toggle value={form.featured === true}   onChange={v => set('featured', v)}  label="FEATURED (homepage)" />
              </div>
            </div>
            <div>
              <p className={labelCls}>TECHNICAL SPECS</p>
              <textarea value={form.specs || ''} onChange={e => set('specs', e.target.value)}
                className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm placeholder:text-white/20 focus:border-primary/60 focus:outline-none px-3 py-2.5 resize-none h-20"
                placeholder="Frame material, dimensions, motor type, etc." />
            </div>
          </div>

          {/* ── EBAY LISTING ─────────────────────────────────────────── */}
          <div className={sectionCls}>
            <span className={sectionLabel}>eBay Listing <span className="text-white/20 normal-case font-normal">(per-product eBay settings — used by the API push & CSV export)</span></span>

            {form.ebay_listed && (
              <div className="px-4 py-2.5 bg-green-500/10 border border-green-500/30 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-400 shrink-0" />
                <p className="text-green-300 text-xs font-bold">LISTED ON EBAY{form.ebay_listing_id ? ` · ${form.ebay_listing_id}` : ''}{form.ebay_group_key ? ` · GROUP ${form.ebay_group_key}` : ''}</p>
              </div>
            )}

            {form.is_variant && (
              <div className="px-4 py-3 border border-blue-500/20 bg-blue-500/[0.03]">
                <p className="text-blue-300 text-[10px] font-bold tracking-widest mb-3">MULTI-VARIATION LISTING</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className={labelCls}>EBAY VARIATION ASPECT</p>
                    <Input value={form.ebay_variation_aspect || 'Colour'} onChange={e => set('ebay_variation_aspect', e.target.value)}
                      className={inputCls} placeholder="Colour" />
                    <p className="text-white/30 text-[10px] mt-1.5 leading-relaxed">The option that differs between variants — e.g. <span className="text-white/50">Colour</span> (Red/Black) or <span className="text-white/50">Model</span> (ZYXEL/Teltonika). Use <span className="text-white/50">Publish group</span> on the eBay page to list all variants as one listing.</p>
                  </div>
                  <div>
                    <p className={labelCls}>EBAY GROUP KEY <span className="normal-case font-normal text-white/20">(optional)</span></p>
                    <Input value={form.ebay_group_key || ''} onChange={e => set('ebay_group_key', e.target.value)}
                      className={inputCls} placeholder="auto (parent item id)" />
                    <p className="text-white/30 text-[10px] mt-1.5 leading-relaxed">Leave blank to group variants by their Square parent item token. Set a custom key to group manual products together.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 sm:col-span-1">
                <p className={labelCls}>EBAY CATEGORY ID <span className="normal-case font-normal text-white/20">(leaf category number)</span></p>
                <Input value={form.ebay_category_id || ''} onChange={e => set('ebay_category_id', e.target.value)}
                  className={inputCls} placeholder="e.g. 13984" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <p className={labelCls}>LISTING DURATION</p>
                <select value={form.ebay_listing_duration || 'GTC'} onChange={e => set('ebay_listing_duration', e.target.value)}
                  className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm h-11 px-3 focus:outline-none focus:border-primary/60 transition-colors">
                  {EBAY_DURATIONS.map(d => <option key={d.value} value={d.value} className="bg-[#111115]">{d.label}</option>)}
                </select>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <p className={labelCls}>SHIPPING SERVICE</p>
                <select value={form.ebay_shipping_service || 'UK_OtherCourier'} onChange={e => set('ebay_shipping_service', e.target.value)}
                  className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm h-11 px-3 focus:outline-none focus:border-primary/60 transition-colors">
                  {EBAY_SHIPPING_SERVICES.map(s => <option key={s.value} value={s.value} className="bg-[#111115]">{s.label}</option>)}
                </select>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <p className={labelCls}>SHIPPING COST (£)</p>
                <Input type="number" min="0" step="0.01" value={form.ebay_shipping_cost ?? 0}
                  onChange={e => set('ebay_shipping_cost', e.target.value === '' ? 0 : parseFloat(e.target.value))}
                  className={inputCls} placeholder="0.00" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <p className={labelCls}>DISPATCH TIME (business days)</p>
                <Input type="number" min="0" max="30" value={form.ebay_dispatch_time_max ?? 2}
                  onChange={e => set('ebay_dispatch_time_max', e.target.value === '' ? 2 : parseInt(e.target.value, 10))}
                  className={inputCls} placeholder="2" />
              </div>
              <div className="col-span-2 sm:col-span-1 flex flex-col gap-3 justify-end pb-1">
                <Toggle value={form.ebay_returns_accepted !== false} onChange={v => set('ebay_returns_accepted', v)} label="RETURNS ACCEPTED" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <p className={labelCls}>RETURNS WITHIN</p>
                <select value={form.ebay_returns_within || 'Days_30'} onChange={e => set('ebay_returns_within', e.target.value)}
                  className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm h-11 px-3 focus:outline-none focus:border-primary/60 transition-colors">
                  {EBAY_RETURNS_WITHIN.map(r => <option key={r.value} value={r.value} className="bg-[#111115]">{r.label}</option>)}
                </select>
              </div>
              <div className="col-span-2 sm:col-span-1">
                <p className={labelCls}>RETURN SHIPPING PAID BY</p>
                <select value={form.ebay_return_shipping_paid_by || 'Buyer'} onChange={e => set('ebay_return_shipping_paid_by', e.target.value)}
                  className="w-full bg-[#0d0d0f] border border-white/10 text-white text-sm h-11 px-3 focus:outline-none focus:border-primary/60 transition-colors">
                  {EBAY_RETURN_PAID_BY.map(r => <option key={r.value} value={r.value} className="bg-[#111115]">{r.label}</option>)}
                </select>
              </div>
              <div>
                <p className={labelCls}>ITEM LOCATION (CITY)</p>
                <Input value={form.ebay_item_location || ''} onChange={e => set('ebay_item_location', e.target.value)}
                  className={inputCls} placeholder="Liverpool" />
              </div>
              <div>
                <p className={labelCls}>ITEM POSTCODE</p>
                <Input value={form.ebay_item_postcode || ''} onChange={e => set('ebay_item_postcode', e.target.value)}
                  className={inputCls} placeholder="L1 8JQ" />
              </div>
              <div className="col-span-2">
                <p className={labelCls}>PAYPAL EMAIL <span className="normal-case font-normal text-white/20">(optional, for immediate payment)</span></p>
                <Input type="email" value={form.ebay_paypal_email || ''} onChange={e => set('ebay_paypal_email', e.target.value)}
                  className={inputCls} placeholder="paypal@example.com" />
              </div>
            </div>
            <p className="text-white/30 text-xs leading-relaxed">
              Title, description, price, quantity, SKU, GTIN and images are taken from the Square fields above. Set the eBay category and shipping here per product, then use <span className="text-white/60 font-bold">List on eBay</span> on the products page to push the listing.
            </p>
          </div>

          {error && <p className="text-red-400 text-xs mt-4">{error}</p>}

          {/* Actions */}
          <div className="flex gap-3 pt-5 mt-5 border-t border-white/5">
            <button type="button" onClick={onClose}
              className="flex-1 border border-white/10 hover:border-white/30 text-white/50 hover:text-white text-xs font-bold tracking-wider py-3 transition-all">
              CANCEL
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider py-3 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? 'SAVING...' : isEdit ? 'SAVE CHANGES' : 'CREATE PRODUCT'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}